// Роли сервиса (040: конституция 3.0.0, принцип XII; research Р1, Р2). Роль на установку назначается
// одна — «Администратор» (client_admin); «Сотрудник» (member) — неявная роль сеанса сотрудника;
// lead — запись владения протоколом (назначение с областью документа, 014, 038), на экране —
// «Владелец» во второй таблице; support — доступ поставщика. Ключи client_admin и lead
// не переименовываются: они записаны в базе, журнале и событиях (research Р1).
import { z } from '../kernel/zod.ts';
import { fieldMessages } from '../kernel/field-messages.ts';

/** Порядок — как в колонках матрицы прав. */
export const roles = ['client_admin', 'member', 'lead', 'support'] as const;

export type Role = (typeof roles)[number];

export const RoleSchema = z.enum(roles);

/** Роли, которые назначает администратор (040, FR-001): «Администратор» и сопровождение. */
export const assignableRoles = ['client_admin', 'support'] as const satisfies readonly Role[];
export type AssignableRole = (typeof assignableRoles)[number];

/** Роль во входящих данных формы — с текстом ошибки поля. */
export const RoleInputSchema = z.enum(assignableRoles, { error: fieldMessages.role });

export interface RoleInfo {
  /** Название с заглавной: заголовок колонки, подпись в списке ролей. */
  readonly title: string;
  /** Название внутри фразы: «Роль администратор снята». */
  readonly inText: string;
  /** Пояснение роли для экрана «Роли и права» (FR-015а; 040, FR-019). */
  readonly description: string;
}

export const roleInfo = {
  client_admin: {
    title: 'Администратор',
    inText: 'администратор',
    description:
      'пользователи, настройки, почта и рассылка, справочник, все протоколы и журнал действий.',
  },
  member: {
    title: 'Сотрудник',
    inText: 'сотрудник',
    description:
      'создаёт протоколы и становится их владельцем; читает протоколы, открытые всем, и справочник.',
  },
  lead: {
    title: 'Владелец',
    inText: 'владелец',
    description:
      'свой протокол: правка, доступ, перевод из Excel, рассылка и вопросы по его пунктам.',
  },
  support: {
    title: 'Сопровождение',
    inText: 'сопровождение',
    description:
      'технические экраны без персональных данных; содержимое — только по временному доступу.',
  },
} as const satisfies Record<Role, RoleInfo>;

/** Эти роли действуют во всей установке (041: областей доступа нет, FR-011). */
export const installationOnlyRoles = [
  'client_admin',
  'member',
  'support',
] as const satisfies readonly Role[];

export function isInstallationOnlyRole(role: Role): boolean {
  return (installationOnlyRoles as readonly Role[]).includes(role);
}

/** Вид пользователя в списке «Пользователи» (040, FR-018): роль или домен почты. */
export const userKinds = ['admin', 'member', 'external', 'support'] as const;
export type UserKind = (typeof userKinds)[number];
export const UserKindSchema = z.enum(userKinds);

export const userKindTitles = {
  admin: 'Администратор',
  member: 'Сотрудник',
  external: 'Внешний',
  support: 'Сопровождение',
} as const satisfies Record<UserKind, string>;

/** Слой ролей на экране «Роли и права», вкладка «Роли» (040, FR-019; конституция XII). */
export interface RoleLayer {
  readonly key: 'company' | 'protocol' | 'item';
  readonly title: string;
  readonly roles: readonly {
    readonly key: string;
    readonly title: string;
    readonly description: string;
  }[];
}

export const roleLayers: readonly RoleLayer[] = [
  {
    key: 'company',
    title: 'В компании',
    roles: [
      { key: 'client_admin', ...pick('client_admin') },
      { key: 'member', ...pick('member') },
      {
        key: 'external',
        title: 'Внешний пользователь',
        description:
          'почта вне доменов компании; видит только протоколы, куда его пригласили, протоколы не создаёт.',
      },
    ],
  },
  {
    key: 'protocol',
    title: 'В протоколе',
    roles: [
      { key: 'owner', ...pick('lead') },
      {
        key: 'editor',
        title: 'Редактор',
        description: 'правит протокол целиком; если задана зона — только пункты зоны.',
      },
      { key: 'reader', title: 'Читатель', description: 'читает, печатает и выгружает.' },
    ],
  },
  {
    key: 'item',
    title: 'В пункте',
    roles: [
      {
        key: 'assignee',
        title: 'Ответственный',
        description:
          'отвечает за пункт: видит его в «Моих задачах» и отвечает по ссылке из письма. Это не роль доступа.',
      },
    ],
  },
];

function pick(role: Role): { title: string; description: string } {
  return { title: roleInfo[role].title, description: roleInfo[role].description };
}
